import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy } from "lucide-react";
import { loadState, fetchLeaderboard } from "../store";
import { formatUSD } from "../mock";
import './leaderboardPreview.css';

const WinnerCard = ({ t, big }) => {
  const stroke = "color-mix(in srgb, var(--a-400) 50%, transparent)";
  const strokeGlow = "color-mix(in srgb, var(--a-500) 60%, transparent)";
  return (
    <div
      className="home-leaderboard-winner group relative rounded-2xl overflow-hidden bg-[#0d0f13] transition-transform duration-300 hover:-translate-y-2"
      data-testid={`leaderboard-preview-winner-${t.rank}`}
      style={{ boxShadow: `inset 0 0 0 1px ${stroke}, 0 0 16px -10px ${strokeGlow}` }}
    >
      {/* Grid texture */}
      <div className="card-grid pointer-events-none absolute inset-0 opacity-60" style={{ zIndex: 0 }} />

      <div className={`relative text-center ${big ? "px-4 pt-7 pb-5" : "px-3 pt-5 pb-4"}`}>
        {/* Rank badge */}
        <div className="relative inline-flex mb-3">
          <span
            className={`home-leaderboard-emblem flex items-center justify-center rounded-full bg-[#0a0c10] ${big ? "h-16 w-16" : "h-12 w-12"}`}
            data-testid={`leaderboard-preview-emblem-${t.rank}`}
            style={{ boxShadow: `0 0 0 2px var(--a-400)` }}
          >
            <Trophy className={big ? "h-7 w-7" : "h-5 w-5"} style={{ color: "var(--a-300)" }} />
          </span>
          <span
            className="home-leaderboard-rank absolute -bottom-1 left-1/2 -translate-x-1/2 flex h-6 w-6 items-center justify-center rounded-full ring-2 ring-[#0d0f13] font-display text-xs font-black text-white"
            data-testid={`leaderboard-preview-rank-${t.rank}`}
            style={{ backgroundColor: "var(--a-500)" }}
          >
            {t.rank}
          </span>
        </div>
        <p data-testid={`leaderboard-preview-player-${t.rank}`} className={`home-leaderboard-value font-semibold text-white truncate ${big ? "text-sm sm:text-base" : "text-xs sm:text-sm"}`}>
          {t.user}
        </p>
        <p data-testid={`leaderboard-preview-wager-label-${t.rank}`} className="home-leaderboard-muted mt-2.5 text-[9px] uppercase tracking-widest text-slate-500">Wagered</p>
        <p data-testid={`leaderboard-preview-wager-${t.rank}`} className={`home-leaderboard-value font-display font-bold text-white tabular-nums ${big ? "text-sm sm:text-base" : "text-[11px] sm:text-sm"}`}>
          {formatUSD(t.wagered)}
        </p>
      </div>
      <div
        className={`home-leaderboard-prize relative border-t ${big ? "py-3" : "py-2.5"} text-center`}
        data-testid={`leaderboard-preview-prize-section-${t.rank}`}
        style={{ borderColor: "color-mix(in srgb, var(--a-400) 20%, transparent)", backgroundColor: "color-mix(in srgb, var(--a-500) 8%, transparent)" }}
      >
        <p data-testid={`leaderboard-preview-prize-label-${t.rank}`} className="home-leaderboard-muted text-[9px] uppercase tracking-widest text-slate-500">Prize</p>
        <p data-testid={`leaderboard-preview-prize-${t.rank}`} className={`home-leaderboard-value font-display font-extrabold tabular-nums text-white ${big ? "text-base sm:text-lg" : "text-xs sm:text-sm"}`}>
          {formatUSD(t.prize)}
        </p>
      </div>
    </div>
  );
};

const LeaderboardPreview = () => {
  const data = loadState();
  const [sheet, setSheet] = useState(null);

  useEffect(() => {
    let active = true;
    fetchLeaderboard("stake").then((s) => {
      if (active) setSheet(s);
    });
    return () => { active = false; };
  }, []);

  const top = Array.isArray(sheet?.entries) ? [...sheet.entries].sort((a, b) => a.rank - b.rank) : [];
  const first = top.find((t) => t.rank === 1);
  const second = top.find((t) => t.rank === 2);
  const third = top.find((t) => t.rank === 3);

  return (
    <section data-testid="home-monthly-leaderboard" className="max-w-6xl mx-auto w-full px-4 sm:px-6 pt-3 sm:pt-4 pb-12 sm:pb-14">
      <div data-testid="leaderboard-preview-panel" className="home-leaderboard-panel relative rounded-2xl glass overflow-hidden p-6 sm:p-8 w-full mx-auto transition-all hover:-translate-y-1 hover:ring-1 hover:ring-emerald-400/40">

          <div className="relative flex flex-col lg:flex-row items-center gap-8">
            {/* Left: prize */}
            <div className="text-center lg:text-left shrink-0 lg:w-56">
              <p data-testid="leaderboard-preview-title" className="home-leaderboard-muted text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-1">Monthly Leaderboard</p>
              <h2 data-testid="leaderboard-preview-prize-pool" className="font-display text-4xl sm:text-5xl font-black headline-gradient leading-none">
                {formatUSD(sheet?.prize_pool ?? data.settings.prizePool).replace('.00', '')}
              </h2>
              <Link
                to="/leaderboard"
                data-testid="leaderboard-preview-view-link"
                className="cta-glow mt-5 inline-flex items-center justify-center rounded-xl bg-emerald-500 hover:bg-emerald-400 px-6 py-3 text-sm font-bold text-emerald-950 transition-all hover:-translate-y-0.5"
              >
                View Leaderboard
              </Link>
            </div>

            {/* Right: overlapping podium — 1st larger & in front, 2nd/3rd smaller behind */}
            <div className="relative flex-1 w-full flex items-center justify-center">
              {top.length ? <>
              <div className="relative z-0 w-[32%] origin-right">
                {second && <WinnerCard t={second} />}
              </div>
              <div className="relative z-20 w-[40%] -mx-4 sm:-mx-5">
                {first && <WinnerCard t={first} big />}
              </div>
              <div className="relative z-0 w-[32%] origin-left">
                {third && <WinnerCard t={third} />}
              </div>
              </> : <p className="text-sm text-slate-400 py-8" data-testid="leaderboard-preview-status">{!sheet ? 'Loading standings…' : sheet.unavailable ? 'Standings temporarily unavailable.' : 'No results for this period.'}</p>}
            </div>
          </div>
      </div>
    </section>
  );
};

export default LeaderboardPreview;
